import React, { useState, useEffect, useRef } from "react";
import {
  MoreVertical,
  ChevronRight,
  Loader2,
  Sparkles,
  ChevronUp,
  UserPlus,
  Check,
  CheckCircle2,
  Edit3,
  Trash2,
} from "lucide-react";
import toast from "react-hot-toast";
import { RegenerateSvg, CopySvg, EditSvg } from "../QuestionnaireSvgs"; 
import Checkbox from "../../../../../Checkbox"; 
import TeamAssignmentView from "./TeamAssignmentView";
import QuestionnaireSourcesListModal from "./QuestionnaireSourcesListModal";
import QuestionnaireSourceViewModal from "./QuestionnaireSourceViewModal";
import api from "../../../../chat/api"; 
import Tooltip from "../../Project Page/Tooltip";

const QuestionCard = ({
  q,
  isSelected,
  onSelect,
  isGenerating,
  isRegeneratingThis,
  isDeleting,
  onDelete,
  onGenerate,
  onRegenerate,
  onOpenAssignModal,
  onAssignSubmit,
  projectId,
  docId,
  orgId,
  fetchQuestions,
  documentPermission,
  currentUserId,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAssignView, setIsAssignView] = useState(false);

  // Edit Question State
  const [isEditing, setIsEditing] = useState(false);
  const [editingQuestionText, setEditingQuestionText] = useState("");
  const [savingQuestionStatus, setSavingQuestionStatus] = useState("idle");

  // Edit Answer State
  const [isEditingAnswer, setIsEditingAnswer] = useState(false);
  const [editingAnswerText, setEditingAnswerText] = useState("");
  const [answerSaveStatus, setAnswerSaveStatus] = useState("idle");

  // Menu & Modal States
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [isRegenerateMenuOpen, setIsRegenerateMenuOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // Sources Modal States
  const [isSourcesListModalOpen, setIsSourcesListModalOpen] = useState(false);
  const [isSourceViewModalOpen, setIsSourceViewModalOpen] = useState(false);
  const [selectedSourceData, setSelectedSourceData] = useState(null);

  const actionMenuRef = useRef(null);
  const regenerateMenuRef = useRef(null);

  const hasAssignedMembers = q.assignedMembers && q.assignedMembers.length > 0;
  
  // Checking permissions and verifying if the user is assigned
  const isRestrictedUser = documentPermission === "editor" || documentPermission === "reviewer";
  const isReviewer = documentPermission === "reviewer";
  const isCurrentUserAssigned = q.assignedMembers?.some(
    (m) => Number(m.user_id) === Number(currentUserId)
  );

  // If the user is an Editor/Reviewer, they MUST be assigned to this question to see the Assign button
  const showAssignButton = !isRestrictedUser || isCurrentUserAssigned;

  // Safe check for sources array length
  const sourceCount = q.sources ? q.sources.length : 0;
  const hasSources = sourceCount > 0;

  // Click outside to close menus
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target)
      ) {
        setIsActionMenuOpen(false);
      }
      if (
        regenerateMenuRef.current &&
        !regenerateMenuRef.current.contains(event.target)
      ) {
        setIsRegenerateMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAssignClick = (e) => {
    // If the user is restricted (Editor/Reviewer), they only get here if they are assigned.
    if (isRestrictedUser) {
      if (!isExpanded) setIsExpanded(true);
      setIsAssignView(!isAssignView);
      return;
    }

    // Admins/Owners logic:
    if (hasAssignedMembers) {
      if (!isExpanded) setIsExpanded(true);
      setIsAssignView(!isAssignView);
      return;
    }
    onOpenAssignModal(e, q.uid);
  };

  const startEditingQuestion = () => {
    setIsEditing(true);
    setEditingQuestionText(q.text || "");
    setIsActionMenuOpen(false);
  };

  const handleSaveEditQuestion = async () => {
    const nextText = (editingQuestionText || "").trim();
    if (!nextText) return toast.error("Question title cannot be empty.");

    setSavingQuestionStatus("saving");
    try {
      const response = await api.post("/api/v1/rfp/questions/edit/", {
        folder_id: projectId,
        doc_id: docId,
        question_uid: q.uid,
        question_text: nextText,
      });
      if (response.status === 200 || response.status === 201) {
        setSavingQuestionStatus("saved");
        setTimeout(async () => {
          await fetchQuestions(true);
          setIsEditing(false);
          setSavingQuestionStatus("idle");
        }, 2000);
      } else throw new Error("Failed to save");
    } catch (error) {
      toast.error("Failed to update question");
      setSavingQuestionStatus("idle");
    }
  };

  const handleCopyAnswer = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied!");
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleSaveAnswer = async () => {
    if (!editingAnswerText.trim()) return;
    setAnswerSaveStatus("saving");
    try {
      const response = await api.post("/api/v1/rfp/questions/edit/", {
        folder_id: projectId,
        doc_id: docId,
        question_uid: q.uid,
        response_text: editingAnswerText,
      });
      if (response.status === 200 || response.status === 201) {
        setAnswerSaveStatus("saved");
        setTimeout(async () => {
          await fetchQuestions(true);
          setIsEditingAnswer(false);
          setAnswerSaveStatus("idle");
        }, 2000);
      } else throw new Error("Failed to save answer");
    } catch (error) {
      toast.error("Failed to save answer");
      setAnswerSaveStatus("idle");
    }
  };

  const handleSelectSource = (source) => {
    setIsSourcesListModalOpen(false);
    setSelectedSourceData(source);
    setIsSourceViewModalOpen(true);
  };

  return (
    <>
      <div
        className={`group relative bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all ${
          isActionMenuOpen ? "z-20" : ""
        }`}
      >
        <div className="absolute left-0 top-0 bottom-0 w-[4px] bg-[#990078] rounded-l-lg"></div>
        <div className="p-4 pl-5">
          <div className="flex items-start gap-4">
            <div className="pt-1">
              <Checkbox
                checked={isSelected}
                onChange={() => onSelect(q.uid)}
                size="sm"
                id={`checkbox-${q.uid}`}
              />
            </div>

            <div className="flex-1">
              <div className="flex justify-between items-start">
                <div className="pr-8 flex-1">
                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <span className="text-[#990078] text-[14px] font-medium">
                        {q.qNumber}
                      </span>
                      <input
                        autoFocus
                        type="text"
                        value={editingQuestionText}
                        onChange={(e) => setEditingQuestionText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveEditQuestion();
                          if (e.key === "Escape") setIsEditing(false);
                        }}
                        className="min-w-[70%] px-3 py-2 text-[14px] font-medium border border-gray-200 rounded-lg focus:outline-none focus:border-[#C95EBE]"
                        disabled={savingQuestionStatus === "saving"}
                      />
                      <button
                        onClick={handleSaveEditQuestion}
                        disabled={savingQuestionStatus === "saving"}
                        className="text-[#C95EBE] text-[14px] font-medium"
                      >
                        {savingQuestionStatus === "saving"
                          ? "Saving..."
                          : "Save"}
                      </button>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="text-[#71717A] text-[14px]"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <h3 className="text-[14px] font-medium text-[#18101C]">
                      <span className="text-[#990078] mr-1">{q.qNumber}</span>{" "}
                      {q.text}
                    </h3>
                  )}
                </div>

                <div className="flex items-center gap-2 relative">
                  {/* Show Assign/View Button only if Owner/Admin OR if the restricted user is actually assigned */}
                  {showAssignButton && (
                    <button
                      className={`w-6 h-6 flex items-center justify-center rounded-full border border-dashed transition-all relative ${
                        isAssignView
                          ? "border-[#C95EBE] bg-purple-50"
                          : "border-gray-400 hover:border-[#C95EBE] hover:bg-purple-50"
                      }`}
                      onClick={handleAssignClick}
                    >
                      <UserPlus
                        size={16}
                        className={`${
                          isAssignView
                            ? "text-[#C95EBE]"
                            : "text-gray-400 group-hover:text-[#C95EBE]"
                        }`}
                      />
                      {hasAssignedMembers && (
                        <div className="absolute -top-[1px] -right-[1px] w-2.5 h-2.5 bg-[#FF3B30] border-2 border-white rounded-full"></div>
                      )}
                    </button>
                  )}

                  <div ref={actionMenuRef} className="relative">
                    <button
                      disabled={isReviewer}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsActionMenuOpen(!isActionMenuOpen);
                      }}
                      className={`p-1.5 rounded-full transition-colors ${
                        isReviewer
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      {isDeleting ? (
                        <Loader2
                          size={16}
                          className="animate-spin text-red-500"
                        />
                      ) : (
                        <MoreVertical size={16} className="text-gray-400" />
                      )}
                    </button>

                    {isActionMenuOpen && !isReviewer && (
                      <div className="absolute right-0 top-8 w-48 bg-white rounded-lg shadow-xl border border-gray-100 z-50">
                        <button
                          onClick={startEditingQuestion}
                          className="w-full text-left px-4 py-3 text-[14px] flex items-center gap-2 hover:bg-gray-50"
                        >
                          <Edit3 size={15} /> Edit Question
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsActionMenuOpen(false);
                            onDelete();
                          }}
                          className="w-full text-left px-4 py-3 text-[14px] text-[#E21919] flex items-center gap-2 hover:bg-red-50"
                        >
                          <Trash2 size={15} /> Remove Question
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="mt-4 mb-2">
                  {!q.isAnswered && !isGenerating ? (
                    <div
                      className="rounded-lg h-[calc(100vh-570px)] flex flex-col items-center justify-center gap-4 border border-[#F5E6F3]"
                      style={{
                        background:
                          "linear-gradient(360deg, #FFFFFFE5 0%, #CEADFF66 100%)",
                      }}
                    >
                      <h4 className="text-[#52525B] text-[16px] font-medium">
                        Generate an AI-drafted answer
                      </h4>
                      <button
                        onClick={onGenerate}
                        className="flex items-center gap-2 px-5 py-2.5 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded-lg"
                      >
                        <Sparkles size={16} /> Generate Answer
                      </button>
                    </div>
                  ) : isGenerating ? (
                    <div className="rounded-lg h-[calc(100vh-570px)] flex flex-col items-center justify-center gap-3 bg-gray-50 border border-gray-100">
                      <Loader2 className="w-8 h-8 animate-spin text-[#C95EBE]" />
                      <span className="text-[#52525B] text-[14px] font-medium animate-pulse">
                        {isRegeneratingThis
                          ? "Regenerating..."
                          : "Generating answer..."}
                      </span>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="relative h-auto max-h-[calc(100vh-500px)] custom-scrollbar ">
                        {/* Response View */}
                        <div
                          className={`relative transition-all duration-700 ease-in-out ${
                            isAssignView
                              ? "opacity-0 translate-y-2 pointer-events-none hidden"
                              : "opacity-100 translate-y-0 block"
                          }`}
                        >
                          <div className="border border-gray-200 rounded-lg p-4 bg-white max-h-[calc(100vh-500px)] overflow-y-auto">
                            {isEditingAnswer ? (
                              <div className="flex flex-col gap-3">
                                <textarea
                                  autoFocus
                                  value={editingAnswerText}
                                  onChange={(e) =>
                                    setEditingAnswerText(e.target.value)
                                  }
                                  className="w-full h-48 p-3 text-[13px] border border-gray-300 rounded-md focus:border-[#C95EBE] outline-none resize-none"
                                  placeholder="Edit answer..."
                                />
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => setIsEditingAnswer(false)}
                                    className="px-3 py-1.5 text-xs text-gray-600"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={handleSaveAnswer}
                                    disabled={
                                      answerSaveStatus === "saving" ||
                                      answerSaveStatus === "saved" ||
                                      editingAnswerText === q.responseText
                                    }
                                    className={`px-3 py-1.5 text-xs rounded-md flex items-center gap-2 text-white ${
                                      answerSaveStatus === "saved"
                                        ? "bg-green-600"
                                        : "bg-[#C95EBE]"
                                    } ${
                                      editingAnswerText === q.responseText
                                        ? "opacity-50 cursor-not-allowed"
                                        : ""
                                    }`}
                                  >
                                    {answerSaveStatus === "saving" ? (
                                      <>
                                        <Loader2
                                          size={12}
                                          className="animate-spin"
                                        />{" "}
                                        Saving...
                                      </>
                                    ) : answerSaveStatus === "saved" ? (
                                      <>
                                        <CheckCircle2 size={12} /> Saved
                                      </>
                                    ) : (
                                      "Save Changes"
                                    )}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-[13px] h-full text-[#18101C] leading-relaxed whitespace-pre-wrap">
                                {q.responseText}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Assign View */}
                        <div
                          className={`relative transition-all duration-700 ease-in-out ${
                            isAssignView
                              ? "opacity-100 translate-y-0 block"
                              : "opacity-0 translate-y-2 pointer-events-none hidden"
                          }`}
                        >
                          <div className="border border-gray-200 rounded-lg bg-[#FAFAFA] overflow-hidden">
                            <TeamAssignmentView
                              projectId={projectId}
                              orgId={orgId}
                              question={q}
                              documentPermission={documentPermission}
                              onAssign={(ids) => onAssignSubmit(ids, q.uid)}
                              onRefresh={() => fetchQuestions(true)}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="h-3"></div>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-1 flex items-center justify-between">
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="flex items-center gap-1.5 text-[12px] text-[#71717A] hover:text-[#C95EBE] font-medium"
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp size={14} /> View Less
                    </>
                  ) : (
                    <>
                      <ChevronRight size={14} /> View Question Details
                    </>
                  )}
                </button>

                {isExpanded &&
                  q.isAnswered &&
                  !isGenerating &&
                  !isEditingAnswer &&
                  !isAssignView && (
                    <div className="flex items-center gap-2">
                      <button
                        disabled={!hasSources}
                        onClick={() => setIsSourcesListModalOpen(true)}
                        className={`text-[13px] font-semibold transition-colors hover:underline ${
                          hasSources
                            ? "text-[#8B55DD] hover:text-[#7A45C9]"
                            : "text-gray-400 cursor-not-allowed opacity-60"
                        }`}
                      >
                        View Sources ({sourceCount})
                      </button>

                      {!isReviewer && (
                        <div
                          className="relative flex items-center justify-center"
                          ref={regenerateMenuRef}
                        >
                          <Tooltip content="Regenerate" placement="top">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsRegenerateMenuOpen(!isRegenerateMenuOpen);
                              }}
                              className={`p-1.5 flex items-center justify-center rounded-full transition-colors ${
                                isRegenerateMenuOpen
                                  ? "bg-purple-100 text-[#C95EBE]"
                                  : "text-gray-400 hover:text-[#C95EBE] hover:bg-purple-100"
                              }`}
                            >
                              <RegenerateSvg />
                            </button>
                          </Tooltip>

                          {isRegenerateMenuOpen && (
                            <div className="absolute top-full right-0 mt-2 w-[180px] bg-white rounded-xl shadow-lg border border-gray-100 z-50 overflow-hidden">
                              {[
                                "Formal Tone",
                                "Concise Response",
                                "Expand Details",
                                "Add Bullet Points",
                              ].map((option) => (
                                <button
                                  key={option}
                                  onClick={() => {
                                    setIsRegenerateMenuOpen(false);
                                    onRegenerate(option);
                                  }}
                                  className="w-full text-left px-4 py-2.5 text-[13px] hover:bg-purple-50 hover:text-[#990078] border-b border-gray-50 last:border-0"
                                >
                                  {option}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      <Tooltip
                        content={isCopied ? "Copied!" : "Copy"}
                        placement="top"
                      >
                        <button
                          onClick={() => handleCopyAnswer(q.responseText)}
                          className={`p-1.5 flex items-center justify-center rounded-full transition-colors ${
                            isCopied
                              ? "text-green-600 bg-green-50"
                              : "text-gray-400 hover:text-[#C95EBE] hover:bg-purple-100"
                          }`}
                        >
                          {isCopied ? <Check size={16} /> : <CopySvg />}
                        </button>
                      </Tooltip>

                      {!isReviewer && (
                        <Tooltip content="Edit" placement="top">
                          <button
                            onClick={() => {
                              setIsEditingAnswer(true);
                              setEditingAnswerText(q.responseText);
                            }}
                            className="p-1.5 flex items-center justify-center rounded-full text-gray-400 hover:text-[#C95EBE] hover:bg-purple-100 transition-colors"
                          >
                            <EditSvg />
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <QuestionnaireSourcesListModal
        isOpen={isSourcesListModalOpen}
        onClose={() => setIsSourcesListModalOpen(false)}
        sources={q.sources}
        onSelectSource={handleSelectSource}
      />

      <QuestionnaireSourceViewModal
        isOpen={isSourceViewModalOpen}
        onClose={() => setIsSourceViewModalOpen(false)}
        data={selectedSourceData}
      />
    </>
  );
};

export default QuestionCard;
